/**
 * Libellés d'écran du tableau des concepts — « Un mot pour le code, un mot pour l'écran »
 * (docs/VOCABULAIRE_ECRAN.md, étape 3). Le code garde `Entree` / `cle` / `entrees` ;
 * l'écran dit concept / nom / précisions.
 */
import { plural } from '../../services/export/plural';
import { MOTS_DE_L_ECRAN } from '../help/motsDeLEcran';

export const ENTREE_TABLE_LABELS = {
  nouveau: 'Nouveau concept',
  partages: 'Concepts partagés',
  grouperParNote: 'Grouper par note',
  autresDossiers: 'Autres dossiers',
  colonnes: {
    concept: 'Concept',
    vocabulaires: 'Vocabulaires',
    definition: 'Définition',
    precisions: 'Précisions',
    voir: 'Voir',
    voirAussi: 'Voir aussi',
    termesSpecifiques: 'T. Spécifiques',
    termesGeneriques: 'T. Génériques',
    dateCreation: 'Date création',
    dateModification: 'Date modification',
  },
  aucunVocabulaire: 'Aucun',
  /* Écran vide commun (C24 SP2) : le titre, puis la phrase qui dit ce qu'est un concept — le mot « Concept » de `help/motsDeLEcran.ts` (C24 SP3). */
  vide: 'Aucun concept pour l’instant',
  videAide: MOTS_DE_L_ECRAN.concept.ligne,
  erreurChargement: 'Impossible de charger vos concepts.',
  /* La portée de la liste (C31 bis SP1) : l'écran vide d'une portée sans concept — le sélecteur en tête garde ses mots (`RAPPROCHER_LABELS.portee`). */
  portee: {
    videDossier: 'Aucun concept dans ce dossier.',
    videVocabulaire: 'Aucun concept dans ce vocabulaire.',
    videAide: 'Changez de portée, ou reliez un passage de ce dossier à un concept.',
    videAideVocabulaire: 'Changez de portée, ou ajoutez un concept à ce vocabulaire.',
    tous: 'Tous mes concepts',
  },
  partageDepuis: (nom: string) => `Concept partagé depuis : ${nom}`,
  supprimerTitre: (n: number) => `Supprimer ${n} concept${n > 1 ? 's' : ''} ?`,
  supprimerCorps: (n: number) => n > 1
    ? `Les ${n} concepts sélectionnés seront définitivement supprimés.`
    : 'Le concept sélectionné sera définitivement supprimé.',
  exclusDesLots: (n: number) => n === 1
    ? 'Un concept partagé est exclu des opérations de masse : modifiez-le depuis le vocabulaire partagé.'
    : `${n} concepts partagés sont exclus des opérations de masse : modifiez-les depuis le vocabulaire partagé.`,
  suppressionPartagesRefusee: (n: number) => n === 1
    ? 'Ce concept partagé ne peut pas être supprimé d’ici : retirez-le depuis le vocabulaire partagé.'
    : `${n} concepts partagés ne peuvent pas être supprimés d’ici : retirez-les depuis le vocabulaire partagé.`,
  /** Les concepts vus depuis une note : vue Tableau, historique, capture, barre d'outils, doublon, aperçu. */
  notes: {
    colonne: 'Concepts',
    filtrerColonne: 'Filtrer par concepts',
    reliesTitre: (n?: number) => (n === undefined ? 'Concepts reliés' : `Concepts reliés (${n})`),
    resumeAjoutes: (n: number) => `+${plural(n, 'concept', 'concepts')}`,
    resumeRetires: (n: number) => `−${plural(n, 'concept', 'concepts')}`,
    citationSansConcept: 'Citation enregistrée, sans le concept',
    captureSansConcept: 'Enregistrée, sans le concept',
    surlignerConcepts: 'Surligner les concepts',
    /** Lecture d'une note (`NotePreviewModal`) — mêmes mots que la page publique d'une note. */
    masquerSurlignages: 'Masquer les surlignages',
    afficherSurlignages: 'Afficher les surlignages',
    montrerPassages: (cle: string) => `Montrer les passages de « ${cle} » dans le texte`,
    trouverConcept: 'Trouver un concept',
    doublonReunis: 'Ses concepts seront réunis avec ceux de la note conservée.',
    /** La référence d'une note : le code n'est plus affiché, il se copie (décision du 29/09/2026). */
    copierReference: 'Copier la référence',
    copierReferenceNote: 'Copier la référence de la note',
    referenceCopiee: 'Référence copiée',
    referenceNonCopiee: 'La référence n’a pas pu être copiée.',
    sansTitre: 'Note sans titre',
    ouvrirNote: 'Ouvrir la note',
    elementSansNom: 'Élément sans nom',
  },
  /**
   * Titres des sections de l'aperçu d'un concept (`EntreePreviewModal`). Règle
   * unique : casse de phrase (comme les titres des fenêtres et des panneaux,
   * DESIGN_SYSTEM.md), précédée du pictogramme de la section.
   */
  apercu: {
    definition: 'Définition',
    noteApplication: "Note d'application",
    precisions: (n: number) => `Précisions (${n})`,
    relations: 'Relations sémantiques',
    noteEditoriale: 'Note éditoriale',
    historique: 'Note historique',
    voir: 'Voir',
    voirAussi: 'Voir aussi',
    termesSpecifiques: 'Termes spécifiques',
    termesGeneriques: 'Termes génériques',
    autresNoms: 'autres noms :',
    variantes: (n: number) => `Variantes de recherche (${n})`,
    sources: (n: number) => `Sources (${n})`,
    nomsParLangue: 'Noms par langue',
  },
  /** Le concept vu des organisations dont je suis membre (C26 SP4, spec § 7) ; `cle` = le nom du concept. */
  mesOrganisations: {
    titre: 'Mes organisations',
    aAussi: (org: string, cle: string) => `${org} a aussi « ${cle} »`,
    naPas: (org: string, cle: string) => `${org} n’a pas « ${cle} ».`,
    ceQueEntend: (org: string, cle: string) => `Ce que ${org} entend par « ${cle} »`,
    voirVocabulaire: 'Voir le vocabulaire',
    direCorrespondance: 'Dire la correspondance',
  },
  /** « Face à face avec… » sur la fiche d'un de mes concepts (C23 SP6) : la découverte sous le filtre des notes que je peux lire. */
  faceAFaceAvec: {
    titre: 'Face à face avec…',
    phrase: (n: number, cle: string) => (n === 1
      ? `Parmi les notes que vous pouvez lire, une personne a marqué un concept qui répond à « ${cle} ».`
      : `Parmi les notes que vous pouvez lire, ${n} personnes ont marqué un concept qui répond à « ${cle} ».`),
    /** La raison du rapprochement (« même nom », « autre forme « X » chez vous », « …, selon vous ») vit dans `faceAFaceLabels.raisons`. */
    passagesDans: (passages: number, notes: number) =>
      `${passages} passage${passages > 1 ? 's' : ''} dans ${notes} note${notes > 1 ? 's' : ''} que vous pouvez lire`,
    bouton: 'Face à face',
    personne: 'Personne, pour l’instant.',
    rienSansLecture: 'Ce que vous ne pouvez pas lire n’apparaît pas.',
    tronque: 'Tout n’est pas montré : les personnes les plus récentes seulement.',
    chargement: 'Recherche parmi les notes que vous pouvez lire…',
    erreur: 'Impossible de lire « Face à face avec… ».',
    reessayer: 'Réessayer',
    /** Prénom inconnu : un lecteur. */
    sansNom: 'Un lecteur',
  },
  aucuneNoteAvecImportance: (importance: number) => `Aucune note ne cite ce concept avec l’importance ${importance}`,
  /** Le panneau des références d'un concept : la note d'importance (1 à 5) posée sur chaque renvoi. */
  references: {
    fermer: 'Fermer le panneau',
    filtrerParImportance: 'Filtrer par importance :',
    importance: (n: number) => `Importance ${n}`,
    nombreAvecImportance: (n: number, importance: number) =>
      `${n} référence${n > 1 ? 's' : ''} d’importance ${importance}`,
    aucuneDeCetteImportance: 'Aucune référence de cette importance',
    /** Filtre « Validés » (C22 SP1) : ne garder que les passages dont la marque est validée. */
    valides: 'Validés',
    /** L'interrupteur de la fiche mobile. */
    validesSeulement: 'Validés seulement',
    aucunValide: 'Aucun passage validé',
  },
} as const;

/** Nom technique posé par défaut sur une précision (`entree1`, `entree2`…) : il ne s'affiche jamais. */
const NOM_TECHNIQUE = /^entree\d*$/i;

/**
 * Une ligne « nom : valeur · nom : valeur » pour la colonne « Précisions »
 * (décision du 27/09/2026 : une seule colonne à la place de « Label 1…7 / Entrée 1…7 »).
 * Une précision sans valeur est ignorée ; un nom technique ou vide laisse la valeur seule.
 */
export function formatPrecisions(items: ReadonlyArray<{ label: string; value: string }> | undefined): string {
  return (items ?? [])
    .map(({ label, value }) => ({ label: (label ?? '').trim(), value: (value ?? '').trim() }))
    .filter(({ value }) => value.length > 0)
    .map(({ label, value }) => (label && !NOM_TECHNIQUE.test(label) ? `${label} : ${value}` : value))
    .join(' · ');
}
